import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MultiTenantService } from '../../../authentication/services/multi-tenant.service';
import { DatatableQueryService } from '@kaiser-software/chassis-core/src/app/datatables/services/datatable-query.service';
import { Subject } from 'rxjs';
import { clone } from '@kaiser-software/chassis-core/src/app/support/helpers';
import { SelectService } from '@kaiser-software/chassis-core/src/app/forms/form/services/select.service';

@Component({
    selector: 'chassis-datatable',
    templateUrl: './datatable.component.html',
    styleUrls: ['./datatable.component.scss']
})
export class DatatableComponent implements OnInit, AfterViewInit {

    /**
     * The list with columns to show in the data table.
     */
    @Input() columns: Array<Object>;

    /**
     * The ajax url to send server side render requests to.
     */
    @Input() ajax: string;

    /**
     * On row click callback.
     */
    @Input() click: (data: any, row: any, index: number, event?) => void;

    /**
     * The number of rows to show on a single page.
     *
     * @type {number}
     */
    @Input() length = 20;

    /**
     * Indicates if server side processing is used.
     */
    @Input() serverSide: boolean;

    /**
     * Indicates if server side processing is used.
     */
    @Input() datatables: boolean;

    /**
     * Data table settings.
     */
    @Input() settings: any = {};

    /**
     * Element to append the search input.
     */
    @Input() searchInput: string;

    /**
     * Element to append the length input.
     */
    @Input() lengthInput: string;

    /**
     * List of data table filter callbacks.
     */
    @Input() filters: Array<Function>;

    @ViewChild('yadcfFilters') public filterDomElement: ElementRef;

    public loading = false;

    public type = 'table';

    public tableData = [];

    /**
     * List of yadcf filters.
     *
     * @type {Array}
     */
    private yadcfFilters: Array<any> = [];

    protected sortedFilterColumns: Subject<any> = new Subject();

    /**
     * The inner data table directive element instance.
     */
    @ViewChild(DataTableDirective) private datatableElement: DataTableDirective;

    /**
     * Emits the data table component instance.
     *
     * @type {EventEmitter<any>}
     */
    @Output() public instance: EventEmitter<this> = new EventEmitter;

    protected defaultConfig = {
        pagingType: 'simple_numbers',
        language: {
            paginate: {
                first: '',
                last: '',
                next: '<i class="pg-arrow_right"></i>',
                previous: '<i class="pg-arrow_left"></i>'
            },
            search: '',
            lengthMenu: '_MENU_'
        },
        deferRender: true,
        dom: `tipB`,
        autoWidth: false,
        pageLength: 20,
        columns: [],
        colReorder: {
            realtime: false
        },
        order: [[0, 'desc']],
        // order: false,
        buttons: ['excel'],
    };

    public constructor(private authService: AuthenticationService,
                       private route: ActivatedRoute,
                       protected tenantService: MultiTenantService,
                       protected router: Router,
                       protected datatableQueryService: DatatableQueryService,
                       protected zone: NgZone,
                       protected selectService: SelectService) {

    }

    public ngOnInit() {
        this.setConfiguration();
        this.setCallbacks();

        if (!this.filters) {
            this.filters = [];
        }

        this.addFilters();
    }

    public ngAfterViewInit() {
        this.applyFilters();
        this.applyYadfcFilters();

        this.datatableElement.dtInstance.then((table: any) => {
            const buttonsElement = $('.datatable-buttons');

            if (buttonsElement.length) {
                if (table.buttons) {
                    table.buttons().container().appendTo(buttonsElement);
                }
            }

            // trigger the filter column sort after reordering
            table.on('column-reorder', () => {
                this.sortFilterColumns();
            });

            this.instance.emit(this);
        });
    }

    /**
     * Sort an array with columns to use for correct filter ordering
     */
    public sortFilterColumns() {
        this.zone.run(() => {
            this.datatableElement.dtInstance.then((table: any) => {
                if (table.colReorder) {
                    const order = table.colReorder.order();

                    this.sortedFilterColumns.next(order.map(index => {
                        return this.columns[index];
                    }));
                }
            });
        });
    }

    /**
     * Set configuration.
     */
    private setConfiguration() {
        this.setDefaultConfiguration();
        this.setColumnsConfiguration();
        this.setQueryParamSearchFilters();

        if (this.ajax) {
            this.setAjaxConfiguration();
        }
    }

    /**
     * Initialize default configuration and extend with input settings.
     */
    private setDefaultConfiguration() {
        this.settings = Object.assign(this.defaultConfig, this.settings);

        // Set serverside processing
        this.settings['serverSide'] = this.serverSide;

        this.settings['preDrawCallback'] = () => {
            this.loading = false;
        };

        // Set pagination total setting
        if (this.length) {
            this.settings['pageLength'] = this.length;
        }
    }

    /**
     * Set column configuration.
     */
    private setColumnsConfiguration() {
        // this.columns = this.columns.map((column: any, index) => {
        //     if (column.filter) {
        //         column.filter.column_number = index;
        //     } else {
        //         column.filter               = {};
        //         column.filter.column_number = index;
        //     }
        //
        //     return column;
        // })

        for (const column in this.columns) {
            if (this.columns.hasOwnProperty(column)) {
                this.settings['columns'][column] = {};

                for (const option in this.columns[column]) {
                    if (this.columns[column].hasOwnProperty(option)) {
                        this.settings['columns'][column][option] = this.columns[column][option];
                    }
                }

                try {
                    if (this.columns[column]['filter']['select_type_options']['ajax']) {
                        this.setColumnAjaxConfiguration(this.columns[column], column);
                    }
                } catch (e) {

                }

            }
        }
    }

    /**
     * Apply search filters given in the route query params.
     */
    private setQueryParamSearchFilters() {
        const params: any = this.route.snapshot.queryParams;

        // Set default paging
        const page   = params.page ? parseInt(params.page, 0) : 1;
        const offset = params.offset ? parseInt(params.offset, 0) : 20;

        this.settings.displayStart = (page - 1) * offset;

        // Set default order
        if (params.orderBy) {
            const columnIndex = this.columns.findIndex((column: any) => column.name === params.orderBy);
            const orderDir    = params.orderDir || 'asc';

            this.settings.order = [[columnIndex, orderDir]];
        }

        // Set an empty array for the search columns with the same
        // length as the columns array. The array contains null
        // values and will when needed be filled with search filters
        // later on
        this.settings.aoSearchCols = this.settings.columns.map(() => {
            return null;
        });

        if (params) {
            let searchParams = params.search;

            if (searchParams) {
                // Create an array of search parameters
                if (!(searchParams instanceof Array)) {
                    searchParams = [searchParams];
                }

                searchParams.forEach((param: string) => {
                    const chunks = param.split(':');

                    // A chunk needs to have a column name and a value
                    if (chunks.length === 2) {
                        const [columnName, value] = chunks;

                        const columnIndex = this.settings.columns.findIndex((item) => {
                            return item.name === columnName;
                        });

                        // The column needs to exist in the list of columns
                        if (columnIndex >= 0) {
                            this.settings.aoSearchCols[columnIndex] = {'sSearch': value};
                        }
                    }
                });
            }
        }

        this.setQueryParamListeners();
    }

    private setQueryParamListeners() {
        this.instance.subscribe(() => {
            this.datatableElement.dtInstance.then((table) => {
                // listen for page changes
                table.on('page.dt', () => {
                    // Scroll back to top
                    window.scrollTo(0, 0);

                    this.rebuildQueryParams(table);
                });

                // listen for ordering changes
                table.on('order.dt', () => {
                    this.rebuildQueryParams(table);
                });

                // listen for filtering changes
                table.on('search.dt', () => {
                    this.rebuildQueryParams(table);
                });

                table.on('draw.dt', () => {
                    this.tableData = table.ajax.json()['data'];
                });
            });
        });
    }

    /**
     * Set ajax configuration.
     */
    private setAjaxConfiguration() {
        this.settings['ajax'] = {
            url: this.ajax,
            beforeSend: (request) => {
                this.loading = true;

                request.setRequestHeader('Authorization', 'Bearer ' + this.authService.getToken());

                // apply multi-tenancy
                if (this.tenantService.isEnabled()) {
                    request.setRequestHeader('X-TenantScope', this.tenantService.getCurrentScope());

                    // subscribe on changes
                    this.tenantService.onScopeChange().subscribe(scope => {
                        request.setRequestHeader('X-TenantScope', scope);
                    });
                }
            },
            error: (error) => {
                if (error.status === 401) {
                    this.authService.refreshToken().subscribe((token) => {
                        // new token received -> now reload the datatable
                        this.datatableElement.dtInstance.then((table: DataTables.Api) => {
                            table.ajax.reload();
                        });
                    });
                }
            }
        };

        if (this.datatables) {
            this.settings['serverSide'] = true;
        } else {
            this.settings['ajax']['dataSrc'] = '';
        }
    }

    /**
     * Set callbacks.
     */
    private setCallbacks() {
        if (this.click) {
            this.setOnClickCallback();
        }
    }

    /**
     * Set on click callback.
     */
    private setOnClickCallback() {
        const self = this;

        this.settings['rowCallback'] = (row, data, index) => {
            $('td', row).unbind('click');
            $('td', row).on('click', (event) => {
                self.click(data, row, index, event);
            });

            return row;
        };
    }

    /**
     * Add default filters.
     */
    private addFilters() {
        this.addFilter((instance, element) => {
            element.on('keyup change', '.filters input', function () {
                instance
                    .column($(this).parent().index() + ':visible')
                    .search(this.value)
                    .draw();
            });
        });

        this.addFilter((instance, element) => {
            if (this.searchInput) {
                $('body').find(this.searchInput).keyup((event) => {
                    instance
                        .search(event.currentTarget['value'])
                        .draw();
                });
            }
        });

        this.addFilter((instance, element) => {
            if (this.lengthInput) {
                $('body').find(this.lengthInput).change((event) => {
                    this.length = event.currentTarget['value'];
                    instance
                        .page.len(this.length)
                        .draw();
                });
            }
        });
    }

    /**
     * Add a filter.
     */
    private addFilter(fn: Function) {
        this.filters.push(fn);
    }

    /**
     * Apply filters.
     */
    private applyFilters() {
        this.datatableElement.dtInstance.then((instance: DataTables.Api) => {
            const element = $('#' + instance.table('').node()['attributes']['id'].value);

            for (const filter of this.filters) {
                filter.apply(this, [instance, element]);
            }
        });
    }

    private applyYadfcFilters() {
        this.sortFilterColumns();

        this.columns.forEach((item, index) => {
            const yadcfFilter: any = {};

            yadcfFilter.column_number            = index;
            yadcfFilter.filter_reset_button_text = '&times;';

            yadcfFilter.filter_container_id = 'yadfc-filter-column-' + (item['name'].split('.').join('-'));

            if ('filter' in item) {
                for (const key of Object.keys(item['filter'])) {
                    yadcfFilter[key] = item['filter'][key];

                    if (key === 'filter_type') {
                        switch (item['filter'][key]) {
                            case 'text':
                                yadcfFilter.style_class              = 'form-control';
                                yadcfFilter.reset_button_style_class = 'btn btn-default btn-inside-input';
                                break;
                            case 'select':
                                yadcfFilter.style_class              = 'form-control';
                                yadcfFilter.reset_button_style_class = 'btn btn-default btn-inside-select';
                                break;
                            case 'date':
                                yadcfFilter.reset_button_style_class = 'btn btn-default btn-inside-input';
                                break;
                            case 'range_number':
                            case 'range_date':
                                yadcfFilter.reset_button_style_class = 'btn btn-default btn-inside-range';
                                break;
                            default:
                                break;
                        }
                    }
                }
            }

            this.yadcfFilters.push(yadcfFilter);
        });

        this.datatableElement.dtInstance.then((table: any) => {
            window['yadcf'].init(table, this.yadcfFilters);
        });
    }

    /**
     * Reset all filters.
     */
    public resetAllFilters() {
        this.datatableElement.dtInstance.then((table: DataTables.Api) => {
            window['yadcf'].exResetAllFilters(table);

            this.rebuildQueryParams(table);

            // reset order
            table.order(this.defaultConfig.order).draw();
        });
    }

    /**
     * Refresh the datatable contents.
     */
    public refresh() {
        this.datatableElement.dtInstance.then((table: DataTables.Api) => {
            table.draw();
        });
    }

    private setDisabledButtonsOnFilters() {
        jQuery('.yadcf-filter-range-date', this.filterDomElement.nativeElement).each(function () {
            $(this).parent().removeClass('inuse');
        });

        jQuery('.yadcf-filter-range-date.inuse', this.filterDomElement.nativeElement).each(function () {
            $(this).parent().addClass('inuse');
        });

        jQuery('.yadcf-filter-range-number', this.filterDomElement.nativeElement).each(function () {
            $(this).parent().removeClass('inuse');
        });

        jQuery('.yadcf-filter-range-number.inuse', this.filterDomElement.nativeElement).each(function () {
            $(this).parent().addClass('inuse');
        });
    }

    rebuildQueryParams(table: DataTables.Api) {
        this.setDisabledButtonsOnFilters();

        const params: any = this.route.snapshot.queryParams;
        const queryParams = Object.assign({}, params);

        // page
        const page = table.page.info();

        queryParams.page   = page.page + 1;
        queryParams.offset = page.length;

        // order
        const order = table.order();

        if (order[0]) {
            queryParams.orderBy  = this.columns[order[0][0]].name;
            queryParams.orderDir = order[0][1];
        }

        // search
        const search       = table.columns().search();
        queryParams.search = [];

        for (let i = 0; i < search.length; i++) {
            const searchValue = search[i];
            const column: any = this.columns[i];

            if (searchValue) {
                queryParams.search.push(column.name + ':' + searchValue);
            }
        }

        this.datatableQueryService.setLastQueryParams(queryParams);

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: queryParams
        });
    }

    private setColumnAjaxConfiguration(column: any, columnNumber) {
        const ajaxOptions = Object.assign({}, column.filter.select_type_options.ajax);

        this.settings['columns'][columnNumber]['filter']['select_type_options']['ajax'] = {
            url: column.filter.select_type_options.ajax.url,
            headers: {
                Authorization: 'Bearer ' + this.authService.getToken(),
                'X-TenantScope': this.tenantService.getCurrentScope()
            },
            processResults: (data) => {
                return {
                    results: data.data.map(row => {
                        return {
                            id: row[ajaxOptions.searchDisplayColumn],
                            text: row[ajaxOptions.searchDisplayColumn]
                        }
                    })
                }
            },
            data: (params: { [key: string]: any }) => {
                params = clone(params);

                delete params._type;

                const columns = ajaxOptions.searchQueryColumn.split(';');
                const query   = params.term;

                params.search = this.selectService.search(
                    query,
                    columns,
                    ajaxOptions.defaultSearchParams ? ajaxOptions.defaultSearchParams.search : {}
                );

                if (ajaxOptions.defaultSearchParams && ajaxOptions.defaultSearchParams.searchJoin) {
                    params.searchJoin = ajaxOptions.defaultSearchParams.searchJoin;
                }

                return params;
            }
        };
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/datatables/components/datatable/datatable.component.ts